import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Alert,
  AlertIcon,
  Spinner,
  useColorModeValue,
} from "@chakra-ui/react";
import ReactMarkdown from "react-markdown";
import { useTranslation } from "react-i18next";
import { PublicNavbar } from "./layout/PublicNavbar";
import { Footer } from "./layout/Footer";
import { useSearchParams } from "react-router-dom";

export const AccountDeletion: React.FC = () => {
  const [content, setContent] = useState<string>("");
  const { i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const confirmationCode = searchParams.get("confirmation_code");
  const bgColor = useColorModeValue("gray.50", "gray.900");
  const contentBg = useColorModeValue("white", "gray.800");
  const textColor = useColorModeValue("gray.800", "gray.100");
  const blockquoteBg = useColorModeValue("orange.50", "gray.700");
  const blockquoteBorder = useColorModeValue("orange.500", "orange.200");

  useEffect(() => {
    const loadContent = async () => {
      try {
        const language = i18n.language;
        const fileName =
          language === "pt-BR"
            ? "/assets/content/account-deletion-pt-BR.md"
            : "/assets/content/account-deletion-en.md";
        const response = await fetch(`${fileName}`);
        const text = await response.text();
        setContent(text);
      } catch (error) {
        console.error("Error loading account deletion content:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadContent();
  }, [i18n.language]);

  return (
    <Box minH="100vh" display="flex" flexDirection="column">
      <PublicNavbar />

      <Box flex="1" bg={bgColor} py={20}>
        <Container maxW="container.lg" mt={20}>
          {confirmationCode && (
            <Alert status="info" mb={6} borderRadius="md">
              <AlertIcon />
              <Box>
                Status da Solicitação de Exclusão
                <br />
                Código de Confirmação: {confirmationCode}
                <br />
                Sua solicitação de exclusão de dados está sendo processada. Este
                processo pode levar até 30 dias para ser concluído.
              </Box>
            </Alert>
          )}

          {isLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minH="400px"
            >
              <Spinner size="xl" />
            </Box>
          ) : (
            <Box
              className="markdown-content"
              bg={contentBg}
              p={8}
              borderRadius="xl"
              shadow="md"
              color={textColor}
              sx={{
                "& blockquote": {
                  borderLeft: "4px solid",
                  borderColor: blockquoteBorder,
                  bg: blockquoteBg,
                  p: 4,
                  my: 4,
                  borderRadius: "md",
                },
                "& blockquote p": {
                  m: 0,
                },
                "& blockquote strong": {
                  color: blockquoteBorder,
                },
                "& a": {
                  color: "blue.500",
                  textDecoration: "underline",
                  _hover: {
                    color: "blue.600",
                  },
                },
                "& h1": {
                  fontSize: "2xl",
                  fontWeight: "bold",
                  mb: 4,
                },
                "& h2": {
                  fontSize: "xl",
                  fontWeight: "bold",
                  mb: 3,
                },
                "& h3": {
                  fontSize: "lg",
                  fontWeight: "bold",
                  mb: 2,
                },
                "& ul": {
                  pl: 4,
                  mb: 4,
                },
                "& li": {
                  mb: 2,
                },
              }}
            >
              <ReactMarkdown>{content}</ReactMarkdown>
            </Box>
          )}
        </Container>
      </Box>

      <Footer />
    </Box>
  );
};
